import { useState, type FormEvent } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import { faqList } from "@/mocks/faq";
import type { Faq } from "@/types/content";

const SUBMIT_URL = "https://readdy.ai/api/form/dahgbhbp14a1h3mr6lt0";
const MAX_LEN = 500;

const CONTACT_CARDS = [
  {
    icon: "ri-mail-send-line",
    title: "内容合作与商务",
    value: "contact@guadian.example.com",
    desc: "榜单合作、内容授权、数据引用等相关事宜，建议在邮件标题注明来意。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "爆料线索投稿",
    value: "tips@guadian.example.com",
    desc: "提供线索时请附上可核实的公开信息，我们会安排编辑跟进核实。",
  },
  {
    icon: "ri-time-line",
    title: "响应时间",
    value: "工作日 10:00 - 18:00",
    desc: "此时间段内留言通常在两个工作日内得到回复，节假日可能顺延。",
  },
];

const TOPIC_OPTIONS = ["爆料线索投稿", "内容纠错与更新", "商务与内容合作", "其他问题"];

export default function Contact() {
  const faqs: Faq[] = faqList.slice(0, 3);

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("留言已提交，我们会尽快查看。");
      formEl.reset();
      setLength(0);
      return;
    }
    formData.delete("contact_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (!isOk || isSpam) {
        setStatus("error");
        setError(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage("留言已提交，我们会尽快查看。");
      formEl.reset();
      setLength(0);
    } catch (err) {
      setStatus("error");
      setError("网络异常，提交未成功，请稍后重试。");
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 瓜点 · 网络爆料雷达",
      url: "https://sunshinelabs.cn/contact",
      description: "通过留言表单或邮箱联系瓜点 · 网络爆料雷达，提供爆料线索、内容纠错或商务合作。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sunshinelabs.cn/" },
        { "@type": "ListItem", position: 2, name: "联系我们", item: "https://sunshinelabs.cn/contact" },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="联系我们 - 爆料线索投稿与合作 | 瓜点爆料雷达"
        description="通过留言表单或邮箱联系瓜点 · 网络爆料雷达，提交网络爆料线索、内容纠错反馈或商务合作需求，工作日两个工作日内回复。"
        keywords="网络爆料,联系我们,爆料线索,内容合作,投稿"
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            联系我们
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是提供一条爆料线索、指出内容中的错误，还是商谈内容合作，都欢迎留下信息。我们会认真对待每一条留言。
          </p>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950">其他联系方式</h2>
            <div className="mt-5 flex flex-col gap-4">
              {CONTACT_CARDS.map((card) => (
                <div
                  key={card.title}
                  className="rounded-lg border border-background-200 bg-background-100 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${card.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                    {card.title}
                  </h3>
                  <p className="mt-1 text-sm font-medium text-primary-600">{card.value}</p>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{card.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
              <h2 className="font-heading text-xl font-bold text-foreground-950">在线留言</h2>
              <p className="mt-2 text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项，我们会妥善保管你的信息。
              </p>

              <form
                data-readdy-form
                id="guadian-contact-form"
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="c-name" className="text-sm font-medium text-foreground-800">
                      称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="c-name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      placeholder="怎么称呼你"
                      className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 placeholder:text-foreground-400"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="c-email" className="text-sm font-medium text-foreground-800">
                      邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="c-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="example@mail.com"
                      className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 placeholder:text-foreground-400"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="c-topic" className="text-sm font-medium text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="c-topic"
                    name="topic"
                    defaultValue={TOPIC_OPTIONS[0]}
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    {TOPIC_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="c-message" className="text-sm font-medium text-foreground-800">
                    留言内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="c-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={MAX_LEN}
                    onChange={(e) => setLength(e.target.value.length)}
                    placeholder="请简要说明你提供的线索或需要咨询的问题，最多 500 字。"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 placeholder:text-foreground-400"
                  ></textarea>
                  <span className="self-end text-xs text-foreground-500">
                    {length} / {MAX_LEN}
                  </span>
                </div>

                <div className="gd-form-field-alt">
                  <label htmlFor="c-contact-alt">备用联系方式</label>
                  <input
                    id="c-contact-alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <label className="flex items-start gap-2.5 text-sm text-foreground-600">
                  <input
                    type="checkbox"
                    name="agree"
                    value="已阅读并同意"
                    required
                    className="mt-1 h-4 w-4 shrink-0 accent-primary-500"
                  />
                  <span>
                    我已阅读并同意网站对留言信息的使用说明，确认所提交内容不涉及他人隐私。
                  </span>
                </label>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    {message}
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                    <i className="ri-error-warning-line text-lg"></i>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">留言前先看看</h2>
          <div className="mt-6 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {faqs.map((f) => (
              <div key={f.q} className="p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">{f.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}